// src/encounter/risk/services/use-risk-bridge.ts
// The encounter page's Risk seam (PROD-2577; closes PROD-2354). The Risk step renders the Studio
// template through the DS QuestionnaireRenderer like every other step, so its answers live in the
// page's one `QuestionnaireResponse` state while the step is on screen — but they PERSIST to
// EncounterMetaData Fall/Pain (`use-risk-assessments.ts`), never to the QR. This hook owns both
// directions; the pure translation is `../lib/risk-qr-bridge.ts`.
//
//   HYDRATE — once per encounter, after BOTH the EncounterMetaData GETs and the page's one-shot QR
//   hydrate have settled (that hydrate REPLACES the response wholesale, so merging earlier would be
//   clobbered). Until then `ready` is false and the page keeps the Risk renderer unmounted: Base UI
//   captures a control's controlled/uncontrolled mode at mount, so a field first drawn with no
//   answer ignores the answer that arrives a render later.
//   MIRROR — every renderer emit is diffed; a changed Risk slice is translated onto the entry heads
//   and handed to `updateFall/PainAssessments` (local + dirty; the hook's batched save sends it).
//   The page strips the same slice before the QR save (`withoutRiskAnswers`), so there is no
//   dual write.
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { QAnswerValue, QItem, QRAnswerItem, QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import {
  entriesFromRiskAnswers,
  freeScreenAnswersOf,
  freeScreenScope,
  projectFreeScreens,
  riskAnswersFromEntries,
  riskAnswersOf,
  riskBridgeScope,
  riskCompletion,
  riskPresentLinkId,
  withCompletion,
  withRiskAnswers,
  withoutRiskAnswers,
  type FreeScreenScope,
  type RiskBridgeScope,
} from '../lib/risk-qr-bridge';
import {
  completeFallAssessment,
  completePainAssessment,
  startFallAssessment,
  startPainAssessment,
  type FallAssessmentEntry,
  type PainAssessmentEntry,
} from '../lib/risk-assessment-types';
import { STEADI_FOLLOW_UP_LINKS, steadiAnswersFromNote, steadiFollowUpFor } from '../lib/steadi-follow-up';
import { clinicWallClockStamp } from '@/shared/lib/clinic-time';
import { useRiskAssessments } from './use-risk-assessments';

export interface UseRiskBridgeArgs {
  encounterId: string | null;
  /** The template items the renderer is handed (gender-filtered). */
  items: readonly QItem[] | undefined;
  /** True once the page's one-shot QR hydrate has had its chance to run for this encounter. */
  responseHydrated: boolean;
  setResponse: Dispatch<SetStateAction<QuestionnaireResponse>>;
  /**
   * PROD-2947 · frame v2 (Andrew's walk, 7 Oct):
   *  · B5 — Fall / Pain save on a pause (`useRiskAssessments` autosave), with no "Done" or step change (since 10 Oct the
   *    pause applies with the switch off too; the reporting and the retry below stay frame v2);
   *  · the pain scale (`risk-pain-scale`) has no seat on the EncounterMetaData wire, so it was stripped from the QR
   *    and saved nowhere — it reloaded empty. In frame v2 it stays a QUESTIONNAIRE answer: saved and read back with the
   *    note, under its own linkId. Absent → as before (switch OFF).
   */
  frameV2?: boolean;
  /**
   * PROD-3128 · frame v2: the CDC STEADI follow-up the page offers under "Fall risk present" on a template that authors
   * no STEADI (`steadiFollowUpFor`). Its answers are read from and written to the Fall record's managed STEADI line only
   * (as Flutter's Fall screen writes it); the page drops them from the QR save (`qrExcluded`). Absent → none.
   */
  steadiFollowUp?: QItem;
}

export interface UseRiskBridge {
  /** The bridged groups and every linkId they own. */
  scope: RiskBridgeScope;
  /** The free NRS / STEADI items (PROD-2791). Their answers stay in the QR and are projected onto the heads. */
  free: FreeScreenScope;
  /** PROD-3128: linkIds whose answers live only in the Fall record (the STEADI follow-up). The QR save drops them. */
  qrExcluded: ReadonlySet<string>;
  /** False while a template authors bridged Risk content whose stored answers are not yet merged
   *  into the response. The page must not mount the Risk renderer until this is true. */
  ready: boolean;
  /** Which stored assessment could not be read. Its edits are not saved (see `useRiskAssessments`),
   *  so the page tells the clinician rather than dropping them silently. */
  readFailed: { fall: boolean; pain: boolean };
  /**
   * Mirror one response change to EncounterMetaData. Returns whether the change should mark the
   * QuestionnaireResponse dirty — false ONLY for an emit that changed Risk answers and nothing
   * else, since the QR does not carry them (a dirty QR on a signed encounter saves an amendment).
   * Stable identity.
   */
  mirror: (prev: QuestionnaireResponse, next: QuestionnaireResponse) => boolean;
  /**
   * The Fall/Pain risk cards' "Done" button (DS `QuestionnaireRenderer` `onRiskDone`, capture
   * mode only — scrips-signal-ds#401). Mirrors production Flutter (`fall_assessment_widget.dart`
   * `onPressed`, `pain_assesment_widget.dart` `onPressed`): marks the head entry
   * `isCompleted: true` with a fresh `createdOn`, through the SAME local-then-batched-save path
   * every other Risk edit uses (`updateFallAssessments`/`updatePainAssessments` — dirty flag set,
   * no network call from here). Flutter's own handler is exactly this shape: it clones the entry
   * with `isCompleted`/`createdOn` and hands it to the bloc's local `AppEventType.save` branch,
   * which only updates in-memory appointment state. Here the POST follows on the autosave pause
   * (`RISK_AUTOSAVE_MS`, every frame since 10 Oct 2026) or the next `flush`, whichever is first. Stable identity.
   */
  onRiskDone: (kind: 'fall' | 'pain') => void;
  /** Save pending Risk edits now (encounter-level save). Returns whether a save was started. */
  flush: () => boolean;
  /** A Fall / Pain save is in flight. */
  saving: boolean;
  /** Frame v2: the last Fall / Pain save failed; its edits are still pending. */
  saveFailed: boolean;
  /** The current Fall / Pain entry heads (what Flutter reads), for read-only display of what the template cannot draw
   *  (7 Oct 2026 ruling: the managed STEADI / NRS lines Flutter writes). */
  heads: { fall: FallAssessmentEntry | undefined; pain: PainAssessmentEntry | undefined };
  /** The server refused a Fall / Pain save because the encounter is signed (PROD-2810; see
   *  `useRiskAssessments`). The page shows it and leaves Risk not completed. */
  saveRefused: boolean;
  /** PROD-3142: which list's save was refused as stale (412) — changed elsewhere since it was read; not sent again. */
  saveConflict: { fall: boolean; pain: boolean };
}

const NO_CONFLICT = { fall: false, pain: false } as const;

function sameValue(a: QAnswerValue, b: QAnswerValue): boolean {
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((x, i) => x === b[i]);
  }
  return a === b;
}

/** Same answers by linkId, order-insensitive. */
export function sameAnswers(a: readonly QRAnswerItem[], b: readonly QRAnswerItem[]): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(a.map((x) => [x.linkId, x]));
  return b.every((y) => {
    const x = byId.get(y.linkId);
    return x !== undefined && sameValue(x.value, y.value) && !!x.aiAuthored === !!y.aiAuthored;
  });
}

const sameEntry = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** How long after the last Fall / Pain edit it is saved, in every frame (frame v2 first, then switch off too, 10 Oct). */
export const RISK_AUTOSAVE_MS = 1200;
/** PROD-3065b · frame v2: a failed Fall / Pain save is retried once, automatically, after this delay. */
export const RISK_RETRY_MS = 3000;

/** The linkIds whose answer differs between two answer lists (added, removed or changed). */
function changedLinkIds(prev: readonly QRAnswerItem[], next: readonly QRAnswerItem[]): string[] {
  const key = (v: unknown) => JSON.stringify(v instanceof Date ? v.toISOString() : v);
  const before = new Map(prev.map((a) => [a.linkId, key(a.value)]));
  const after = new Map(next.map((a) => [a.linkId, key(a.value)]));
  return [...new Set([...before.keys(), ...after.keys()])].filter((id) => before.get(id) !== after.get(id));
}

const NONE: ReadonlySet<string> = new Set();

export function useRiskBridge({ encounterId, items, responseHydrated, setResponse, frameV2 = false, steadiFollowUp }: UseRiskBridgeArgs): UseRiskBridge {
  // Fall / Pain save on a pause in EVERY frame (10 Oct 2026, Andrew): with the switch off they used to wait for a step
  // change, so a refresh or a closed tab while still on Risk lost them (dev repro). Reporting and the retry stay frame v2.
  const risk = useRiskAssessments(
    encounterId,
    frameV2 ? { autosaveMs: RISK_AUTOSAVE_MS, reportFailures: true, retryMs: RISK_RETRY_MS } : { autosaveMs: RISK_AUTOSAVE_MS },
  );
  const scope = useMemo(() => riskBridgeScope(items, frameV2 ? { keepUnseatedInQr: true } : {}), [items, frameV2]);
  // NRS / STEADI answers stay in the QR but are PROJECTED onto the entry heads (PROD-2791), so the step
  // also waits for the heads to load before it can be edited — a projection onto an unloaded head would
  // push a fresh entry in front of the stored one.
  // PROD-3128: the STEADI follow-up joins the free screens for the projection onto the Fall head (only frame v2 offers it).
  const followUp = frameV2 ? steadiFollowUp : undefined;
  const freeItems = useMemo(() => (followUp ? [...(items ?? []), followUp] : items), [items, followUp]);
  const free = useMemo(() => freeScreenScope(freeItems), [freeItems]);
  // The QR never carries a STEADI answer on a template that authors no STEADI (frame v2), whether or not the follow-up
  // is offered right now: a date of birth that arrives later can withdraw it after answers were read back (Codex P2).
  const qrExcluded = useMemo(() => (frameV2 && steadiFollowUpFor(items) ? STEADI_FOLLOW_UP_LINKS : NONE), [frameV2, items]);
  const bridged = scope.linkIds.size > 0 || free.linkIds.size > 0;

  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  // PROD-3128: the encounter whose STEADI follow-up answers have been read back from the Fall record.
  const [steadiFor, setSteadiFor] = useState<string | null>(null);
  const { isLoaded, serverFallAssessments, serverPainAssessments } = risk;

  // Sync FROM EncounterMetaData (an external store) into the response — one shot per encounter,
  // computed HERE IN RENDER rather than in an effect. Every input the merge needs (`bridged`,
  // `responseHydrated`, `isLoaded`, the server heads) is already resolved by the time this hook
  // runs — an effect would only add a second render pass after the one that already has
  // everything it needs. This is React's "adjust state when an input changes" shape: guarded by
  // `hydratedFor` so it fires at most once per encounter, and it never lets a frame commit with
  // `ready` true ahead of the merge (an effect-based version could not promise that any more
  // tightly — it would just move the same guarantee one render later).
  if (encounterId && bridged && responseHydrated && isLoaded && hydratedFor !== encounterId) {
    setHydratedFor(encounterId);
    if (followUp) setSteadiFor(encounterId);
    const answers = [
      ...riskAnswersFromEntries(items, serverFallAssessments?.[0], serverPainAssessments?.[0], { presentOnlyWhenCompleted: frameV2 }),
      // PROD-3128: the follow-up's answers, read back from the Fall record's managed STEADI line.
      ...(followUp ? steadiAnswersFromNote(followUp, serverFallAssessments?.[0]?.notePlainText) : []),
    ];
    // Functional merge: never clobbers an edit, and replaces any stale Risk answer a legacy QR row
    // still carried — EncounterMetaData is the answer store.
    setResponse((prev) => withRiskAnswers({ ...prev, answers: prev.answers.filter((a) => !qrExcluded.has(a.linkId)) }, scope, answers));
  }
  // PROD-3128 (Codex P2): a follow-up offered only AFTER that one-shot (the template or the date of birth arrived later)
  // still reads the Fall record's managed STEADI line back before it can be edited, from the CURRENT head (it holds any
  // edit since). Otherwise a first answer would rewrite the line without the answers already in it. Like the one-shot
  // above it runs in render, so the merged answers commit with the follow-up: `ready` needs no extra gate.
  if (encounterId && followUp && hydratedFor === encounterId && steadiFor !== encounterId) {
    setSteadiFor(encounterId);
    const note = risk.fallAssessments[0]?.notePlainText;
    setResponse((prev) => ({
      ...prev,
      answers: [...prev.answers.filter((a) => !STEADI_FOLLOW_UP_LINKS.has(a.linkId)), ...steadiAnswersFromNote(followUp, note)],
    }));
  }

  // The latest committed values, for the stable `mirror` below — synced after every commit, so a
  // user event (which always follows a commit) reads the current heads. This one legitimately stays
  // an effect: writing a ref during RENDER is unsafe (react-hooks/refs — a Strict-Mode double-render
  // would mutate it twice before either commits), so "after every commit" is the correct place, not
  // a smell to remove.
  const latest = useRef({ scope, free, items, freeItems, followUp, risk, frameV2, encounterId });
  useEffect(() => {
    latest.current = { scope, free, items, freeItems, followUp, risk, frameV2, encounterId };
  });
  // Frame v2 (Scrips Agent review at 665cde84):
  //  · `answered` — the Risk linkIds the clinician changed in this session; only those count as a result
  //    (`riskCompletion`), never a value loaded from the record.
  //  · `started` — the Fall / Pain record this session started. An edit to a record completed BEFORE it starts a new
  //    head record and leaves the completed one untouched, as Flutter's "start" does when the first record is
  //    completed (risk_assessment_screen.dart:151); the record this session started is then edited in place.
  //  · Both belong to ONE encounter (Scrips Agent review at bea4b95b): they reset when `encounterId` changes, so the page
  //    no longer has to be keyed per appointment for them to be safe. The reset runs in `mirror` (an event, where a ref
  //    write is safe), the first time it sees a new encounter.
  const answered = useRef(new Set<string>());
  const started = useRef<{ fall?: string; pain?: string }>({});
  const refsFor = useRef(encounterId);

  const mirror = useCallback((prev: QuestionnaireResponse, next: QuestionnaireResponse): boolean => {
    const { scope: s, free: f, items: it, freeItems: fit, followUp: fu, risk: r } = latest.current;
    if (refsFor.current !== latest.current.encounterId) {
      refsFor.current = latest.current.encounterId;
      answered.current = new Set();
      started.current = {};
    }
    if (s.linkIds.size === 0 && f.linkIds.size === 0) return true;
    const prevRisk = riskAnswersOf(prev, s);
    const nextRisk = riskAnswersOf(next, s);
    const riskChanged = !sameAnswers(prevRisk, nextRisk);
    const freeChanged = f.linkIds.size > 0 && !sameAnswers(freeScreenAnswersOf(prev, f), freeScreenAnswersOf(next, f));
    if (riskChanged || freeChanged) {
      const v2 = latest.current.frameV2;
      const nextFree = freeScreenAnswersOf(next, f);
      if (v2) for (const id of changedLinkIds([...prevRisk, ...freeScreenAnswersOf(prev, f)], [...nextRisk, ...nextFree])) answered.current.add(id);
      const fallHead = r.fallAssessments[0] ?? startFallAssessment([])[0];
      const painHead = r.painAssessments[0] ?? startPainAssessment([])[0];
      // `all`: a NEW record carries every current answer, bridged and free-screen alike — not only the set that changed
      // (Codex P1); an in-place update applies only what changed, as before.
      const derive = (fallBase: FallAssessmentEntry, painBase: PainAssessmentEntry, all = false) => {
        // Frame v2 (7 Oct 2026 ruling): Morse rows and the Wong-Baker band are read-only history — never written.
        let out = riskChanged || all
          ? entriesFromRiskAnswers(it, nextRisk, fallBase, painBase, { licensedReadOnly: v2 })
          : { fall: fallBase, pain: painBase };
        // PROD-3128: with the STEADI follow-up, the clinician's "Fall risk present" stays the record's `isPresent`.
        if (freeChanged || (all && f.linkIds.size > 0)) out = projectFreeScreens(fit, nextFree, out.fall, out.pain, { steadiSetsPresent: !fu });
        // Frame v2 (no "Done"): Flutter's Done applied when the assessment's result is recorded (`riskCompletion`) —
        // isCompleted true and createdOn set once; a partial assessment stays not completed.
        if (v2) {
          const done = riskCompletion(it, [...nextRisk, ...nextFree], answered.current);
          // PROD-3065a: Flutter's own record-time format, on the clinic's clock (`clinicWallClockStamp`), never UTC.
          const now = clinicWallClockStamp(new Date());
          out = { fall: withCompletion(out.fall, done.fall, now), pain: withCompletion(out.pain, done.pain, now) };
        }
        return out;
      };
      const inPlace = derive(fallHead, painHead);
      // Frame v2: a changed record that was completed before this session → a new head record (Flutter's "start").
      const newFall = v2 && fallHead.isCompleted && fallHead.id !== started.current.fall && !sameEntry(inPlace.fall, fallHead);
      const newPain = v2 && painHead.isCompleted && painHead.id !== started.current.pain && !sameEntry(inPlace.pain, painHead);
      // The new record carries the completed record's result (Scrips Agent review at bea4b95b): its "present" counts as
      // answered, so a detail-only edit leaves the new head completed, as Flutter's Start then Done would save it.
      for (const kind of [newFall && 'fall', newPain && 'pain'] as const) {
        const id = kind ? riskPresentLinkId(it, kind) : undefined;
        if (id) answered.current.add(id);
      }
      const fresh = newFall || newPain
        ? derive(newFall ? startFallAssessment([])[0] : fallHead, newPain ? startPainAssessment([])[0] : painHead, true)
        : inPlace;
      // A record this session edits while it is still open is this session's too: once the session completes it, later
      // edits stay on it (createdOn never moves) rather than starting yet another record.
      if (newFall) {
        started.current.fall = fresh.fall.id;
        r.updateFallAssessments([fresh.fall, ...r.fallAssessments]);
      } else if (!sameEntry(inPlace.fall, fallHead)) {
        if (!fallHead.isCompleted) started.current.fall = fallHead.id;
        r.updateFallAssessments([inPlace.fall, ...r.fallAssessments.slice(1)]);
      }
      if (newPain) {
        started.current.pain = fresh.pain.id;
        r.updatePainAssessments([fresh.pain, ...r.painAssessments]);
      } else if (!sameEntry(inPlace.pain, painHead)) {
        if (!painHead.isCompleted) started.current.pain = painHead.id;
        r.updatePainAssessments([inPlace.pain, ...r.painAssessments.slice(1)]);
      }
    }
    const questionnaireChanged = !sameAnswers(withoutRiskAnswers(prev, s).answers, withoutRiskAnswers(next, s).answers);
    return questionnaireChanged || !riskChanged;
  }, []);

  // Same stable-identity pattern as `mirror` above: reads the latest heads through the ref rather
  // than closing over `risk` directly, so this callback's identity never changes across renders.
  const onRiskDone = useCallback((kind: 'fall' | 'pain') => {
    const { risk: r } = latest.current;
    if (kind === 'fall') {
      r.updateFallAssessments(completeFallAssessment(r.fallAssessments));
    } else {
      r.updatePainAssessments(completePainAssessment(r.painAssessments));
    }
  }, []);

  return {
    scope,
    free,
    qrExcluded,
    ready: !bridged || (!!encounterId && hydratedFor === encounterId),
    readFailed: risk.readFailed,
    mirror,
    onRiskDone,
    flush: risk.flush,
    saving: risk.saving,
    saveFailed: risk.saveFailed,
    heads: { fall: risk.fallAssessments[0], pain: risk.painAssessments[0] },
    saveRefused: risk.saveRefused,
    // The real hook always answers; a test double of it may not (36 page suites stub it), which reads as no conflict.
    saveConflict: risk.saveConflict ?? NO_CONFLICT,
  };
}
